import { useEffect, useRef, useState } from "react";
import LoaderComponent from "./Components/Generic/LoaderComponent";
import { createSearchParams, Outlet, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import SideBarMenu from "./Components/SideNav/SideBarMenu";
import styled from "styled-components";
import { useProjectContext } from "./context/useProjectContext";
import { ROUTE_MAP, productSlugForPath } from "./constants/productConstants";
import { showGenericError } from "./helper/Notifications";
import { AnimatePresence } from "framer-motion";
import CreateSubscriptionModal from "./Components/SideNav/CreateSubscriptionModal";
import SupportModal from "./Components/Support/SupportModal";
import SalesModal from "./Components/Support/SalesModal";
import { lastIndexOf } from "lodash";
import { useKeysContext } from "./context/useKeysContext";
import { useLicensingContext } from "./context/useLicensingContext";

function App() {

  const {
    getProjects,
    selectedProject,
    setSelectedProject,
    getProjectUsers,
    currentMembership,
    isAdmin,
    hasFullProductAccess,
    canAccessProduct,
  } = useProjectContext();
  const { getPlatformSubscription, subscription, setSubscription, getPlatformProducts } = useLicensingContext();
  const currentProjectIdRef = useRef(null);

  const { getKeys, getEmbedKeys } = useKeysContext();
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const [createNewProjectModalOpen, setCreateNewProjectModalOpen] =
    useState(false);
  const [createFirstProjectModalOpen, setCreateFirstProjectModalOpen] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [showSalesModal, setShowSalesModal] = useState(false);

  const firstLoginRef = useRef(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const redirectIfNoAccess = () => {
    if (!currentMembership || hasFullProductAccess) return;

    const slugs = currentMembership.product_slugs;
    const adminOnlyPaths = ["/dashboard", "/subscriptions"];
    const currentPath = location.pathname;

    if (adminOnlyPaths.includes(currentPath)) {
      navigateToFirstAccessiblePage(slugs);
      return;
    }

    const requiredSlug = productSlugForPath(currentPath);
    if (requiredSlug && !canAccessProduct(requiredSlug)) {
      navigateToFirstAccessiblePage(slugs);
    }
  };

  const navigateToFirstAccessiblePage = (slugs) => {
    for (const slug of slugs) {
      if (ROUTE_MAP[slug]) {
        navigate({
          pathname: ROUTE_MAP[slug],
          search: createSearchParams(searchParams).toString(),
        });
        return;
      }
    }

    navigate({
      pathname: "/glossary",
      search: createSearchParams(searchParams).toString(),
    });
  };

  const handleGetProjects = () => {
    setLoading(true);

    getProjects(
      (response) => {
        handleProjectResponse(response.data.response);
        setLoading(false)
      },
      (error) => {
        setLoading(false)
        showGenericError();
        console.log(error)
      },
    )
  }

  const handleProjectResponse = (projects) => {
    // Check for pendingProjectId first (returning from Stripe)
    let pendingProjectId = null;
    try {
      pendingProjectId = localStorage.getItem('pendingProjectId');
      if (pendingProjectId) {
        const pendingProject = projects.find((p) => String(p.id) === String(pendingProjectId));
        if (pendingProject) {
          // Clear the pending project ID
          localStorage.removeItem('pendingProjectId');
          // Navigate to the new project
          setSearchParams((params) => {
            params.set("project_id", String(pendingProject.id));
            return params;
          });
          setSelectedProject(pendingProject);
          setLoading(false);
          handleLocation(); // Redirect from / to /dashboard if needed
          return;
        } else {
          // Project not found yet, might still be processing - clear it
          localStorage.removeItem('pendingProjectId');
        }
      }
    } catch (e) {
      // ignore storage errors
    }

    if (searchParams.get("project_id")) {
      const found = projects.find(
        (item) => String(item.id) === String(searchParams.get("project_id"))
      );
      if (found) {
        setSelectedProject(found);
      } else {
        // Invalid project_id in URL; restore from storage or fallback to first
        if (projects.length > 0) {
          let restored;
          try {
            const savedId = localStorage.getItem("selectedProjectId");
            if (savedId) {
              restored = projects.find((p) => String(p.id) === String(savedId));
            }
          } catch (e) {}
          const projectToUse = restored || projects[0];
          setSearchParams((params) => {
            params.set("project_id", String(projectToUse.id));
            return params;
          });
          setSelectedProject(projectToUse);
          setLoading(false);
        }
      }
    } else {
      if (projects.length > 0) {
        // Try to restore from localStorage if no project_id in URL
        let restored = undefined;
        try {
          const savedId = localStorage.getItem("selectedProjectId");
          if (savedId) {
            restored = projects.find((p) => String(p.id) === String(savedId));
          }
        } catch (e) {
          // ignore storage errors
        }

        const projectToUse = restored || projects[0];
        firstLoginRef.current = true;
        setSearchParams((params) => {
          params.set("project_id", String(projectToUse.id));
          return params;
        });
        setSelectedProject(projectToUse);
        setLoading(false);
      } else {
        setCreateFirstProjectModalOpen(true)
      }
    }
    handleLocation();
  };

  const handleGetKeys = (projectId) => {
    setLoading(true)
    getKeys(
      projectId,
      1, // page
      25, // items per page
      (response) => { setLoading(false) },
      (error) => {
        setLoading(false)
      }
    )
    getKeys(
      projectId,
      1, // page
      25, // items per page
      () => {},
      () => {},
      null,
      "realtime_api"
    )
    getKeys(
      projectId,
      1, // page
      25, // items per page
      () => {},
      () => {},
      null,
      "meetings_api"
    )
  }

  const handleGetEmbedKeys = (projectId) => {
    setLoading(true)
    getEmbedKeys(
      projectId,
      1, // page
      25, // items per page
      (response) => {
        if (currentProjectIdRef.current !== projectId) return;
        setLoading(false)
      },
      (error) => {
        setLoading(false)
      }
    )
  }

  const loadProjectDataForMembership = (projectId) => {
    if (isAdmin) {
      handleGetSubscriptionDetails(projectId);
      handleGetProjectUsers(projectId);
      handleGetTeamsProjectUsers(projectId);
      handleGetKeys(projectId);
      handleGetEmbedKeys(projectId);
      return;
    }

    setSubscription(null);

    const productFetchMap = {
      "api": () => getKeys(projectId, 1, 25, () => {}, () => {}),
      "realtime_api": () => getKeys(projectId, 1, 25, () => {}, () => {}, null, "realtime_api"),
      "meetings_api": () => getKeys(projectId, 1, 25, () => {}, () => {}, null, "meetings_api"),
      "website_translation": () => handleGetEmbedKeys(projectId),
      "meetings": () => handleGetProjectUsers(projectId),
      "emotii_for_teams": () => handleGetTeamsProjectUsers(projectId),
    };

    Object.entries(productFetchMap).forEach(([slug, fetchProductData]) => {
      if (canAccessProduct(slug)) fetchProductData();
    });
    redirectIfNoAccess();
  }

  const handleGetSubscriptionDetails = (projectId) => {
    setLoading(true);
    getPlatformSubscription(
      projectId,
      () => {
        setLoading(false);
      },
      () => {
        setLoading(false);
        setSubscription(null);
      }
    );
  };

  const handleGetProjectUsers = (projectId) => {
    setLoading(true);
    getProjectUsers(
      projectId, "", 1, 20, "", "meetings",
      () => { setLoading(false); },
      (error) => { setLoading(false); showGenericError(); console.log(error); }
    );
  };

  const handleGetTeamsProjectUsers = (projectId) => {
    getProjectUsers(
      projectId, "", 1, 20, "", "emotii_for_teams",
      () => {},
      () => {}
    );
  };

  const handleGetPlatformProducts = (projectId) => {
    getPlatformProducts(
        projectId,
        () => {},
        () => {}
    );
  }

  const handleLocation = () => {
    if (location.pathname === "/") {
      navigate({
        pathname: "/dashboard",
        search: createSearchParams(searchParams).toString()
      })
    }
  }


  useEffect(() => {
    handleGetProjects()
  }, [])

  useEffect(() => {
    if (!selectedProject) {
      return
    }
    currentProjectIdRef.current = selectedProject.id;
    setSubscription(undefined);
    loadProjectDataForMembership(selectedProject.id);
    handleGetPlatformProducts(selectedProject.id);
  }, [selectedProject])



  return (
    <Container>
      <SideBarMenu
        setCreateNewProjectModalOpen={setCreateNewProjectModalOpen}
        createNewProjectModalOpen={createNewProjectModalOpen}
        setShowSupportModal={setShowSupportModal}
      />
      <OutletWrapper>
        <Outlet
          context={{
            loading,
            setLoading,
            setCreateFirstProjectModalOpen,
            setShowSupportModal,
            setShowSalesModal
          }}
        />

      </OutletWrapper>

      <AnimatePresence>
        {createNewProjectModalOpen && (
          <CreateSubscriptionModal
            isOpen={createNewProjectModalOpen}
            onOpenChange={setCreateNewProjectModalOpen}
          />
        )}

        {createFirstProjectModalOpen && (
          <CreateSubscriptionModal
            isOpen={createFirstProjectModalOpen}
            onOpenChange={setCreateFirstProjectModalOpen}
            isFirstProject={true}
          />
        )}

        {showSupportModal && (
          <SupportModal setIsModalOpen={setShowSupportModal} />
        )}

        {showSalesModal && (
          <SalesModal setIsModalOpen={setShowSalesModal} />
        )}
      </AnimatePresence>
    </Container>


  );
}

export default App;

const Container = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const OutletWrapper = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: auto;
  
  @media (max-width: 768px) {
    margin-top: 60px; /* Account for fixed mobile sidebar at top */
    height: calc(100% - 60px);
  }
`;
